/**
 * Diagram pieces for the SNF walker panel.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { GradeVector } from "../../components/GradeVector.js";
import type { Grade } from "../../algorithms/grade.js";

interface PhaseCardProps {
  readonly phase: string;
  readonly phaseName: string;
  readonly accent: string;
  readonly caption: string;
  readonly grade: Grade | null;
  readonly mono?: string;
}

export function PhaseCard({
  phase,
  phaseName,
  accent,
  caption,
  grade,
  mono,
}: PhaseCardProps): React.JSX.Element {
  return (
    <div
      className="flex min-w-0 flex-col gap-2.5 rounded-xl border border-solid px-3.5 py-3"
      style={{
        background: `linear-gradient(180deg, ${accent}10, rgba(5,8,16,0.55))`,
        borderColor: `${accent}55`,
      }}
    >
      <div className="flex items-baseline gap-2">
        <span
          className="font-mono text-[18px] font-extrabold"
          style={{
            color: accent,
            textShadow: `0 0 14px ${accent}77`,
          }}
        >
          {phase}
        </span>
        <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--tcc-muted)]">
          {phaseName}
        </span>
      </div>
      {grade ? (
        <GradeVector grade={grade} compact label={`${phase} grade`} />
      ) : (
        <div className="break-words rounded-lg border border-dashed border-[var(--tcc-border)] bg-[rgba(5,8,16,0.65)] px-2.5 py-2 font-mono text-[11.5px] text-[var(--tcc-muted)]">
          {mono ?? "—"}
        </div>
      )}
      <div className="font-mono text-[11.5px] leading-[1.45] text-[var(--tcc-text)]">
        {caption}
      </div>
    </div>
  );
}

export function Arrow(): React.JSX.Element {
  return (
    <div
      aria-hidden
      className="inline-flex items-center justify-center font-mono text-[16px] font-bold text-[var(--tcc-muted)]"
    >
      ›
    </div>
  );
}

interface TruthRowProps {
  readonly quantity: string;
  readonly atB2vsB4: string;
  readonly verdict: string;
  readonly verdictColor: string;
}

export function TruthRow({
  quantity,
  atB2vsB4,
  verdict,
  verdictColor,
}: TruthRowProps): React.JSX.Element {
  return (
    <div className="grid grid-cols-[minmax(0,220px)_minmax(0,1fr)_minmax(0,1fr)] items-center border-b border-[var(--tcc-border)] px-3.5 py-2.5 text-[13px]">
      <span className="font-mono font-bold text-[var(--tcc-bright)]">
        {quantity}
      </span>
      <span className="text-[var(--tcc-text)]">{atB2vsB4}</span>
      <span
        className="font-mono text-[12.5px] font-bold"
        style={{ color: verdictColor }}
      >
        {verdict}
      </span>
    </div>
  );
}
